import type { Action, Thunk } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { StoreModel } from '../..';
import parseStatusCode from '../../errors/parseStatusCode';
import type { Injections } from '../../services/injections';
import type { FulfillmentContext } from '../../services/FP_FulfillmentContextService/types';
import type { ApplicationErrorOptions } from '../../../types';
import { BULK_ARCHIVE_ABORTED_REASON } from '~/types/bulkArchive';
import type { BulkArchiveResult, BulkArchiveFailure } from '~/types/bulkArchive';
import { extractErrorReason } from '~/utils/extractErrorReason';
export interface BulkContextLinkResult {
  failedContexts: string[];
  linkedContexts: string[];
}

export interface FulfillmentContextsModel {
  fulfillmentContextsMap: Record<string, FulfillmentContext>;
  setFulfillmentContexts: Action<FulfillmentContextsModel, FulfillmentContext[]>;
  setFulfillmentContext: Action<FulfillmentContextsModel, FulfillmentContext>;
  fetchFulfillmentContextsLoading: boolean;
  setFetchFulfillmentContextsLoading: Action<FulfillmentContextsModel, boolean>;
  fetchFulfillmentContexts: Thunk<FulfillmentContextsModel, void, Injections, StoreModel, Promise<void>>;
  fetchFulfillmentContextsFailure: Action<FulfillmentContextsModel, ApplicationErrorOptions>;

  bulkLinkOriginToContexts: Thunk<
    FulfillmentContextsModel,
    { originId: string; fulfillmentContextIds: string[] },
    Injections,
    StoreModel,
    Promise<BulkContextLinkResult>
  >;
  setLinkOriginToContextsResults: Action<FulfillmentContextsModel, BulkContextLinkResult>;
  bulkLinkOriginContextResults: BulkContextLinkResult;

  archiveFulfillmentContext: Thunk<FulfillmentContextsModel, { id: string }, Injections, StoreModel, Promise<void>>;

  bulkArchiveFulfillmentContexts: Thunk<
    FulfillmentContextsModel,
    { ids: string[]; signal?: AbortSignal },
    Injections,
    StoreModel,
    Promise<BulkArchiveResult>
  >;
}

export const fulfillmentContextsModel: FulfillmentContextsModel = {
  fulfillmentContextsMap: {},
  bulkLinkOriginContextResults: { failedContexts: [], linkedContexts: [] },
  setFulfillmentContexts: action((state, contracts) => {
    const fulfillmentContextsMap = contracts.reduce<Record<string, FulfillmentContext>>((acc, curr) => {
      acc[curr.fulfillmentContextId] = curr;
      return acc;
    }, {});
    state.fulfillmentContextsMap = fulfillmentContextsMap;
  }),
  setFulfillmentContext: action((state, fulfillmentContext) => {
    state.fulfillmentContextsMap[fulfillmentContext.fulfillmentContextId] = fulfillmentContext;
  }),
  fetchFulfillmentContextsLoading: false,
  setFetchFulfillmentContextsLoading: action((state, isLoading) => {
    state.fetchFulfillmentContextsLoading = isLoading;
  }),
  fetchFulfillmentContexts: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.setFetchFulfillmentContextsLoading(true);
      const { fulfillmentContextService } = injections;
      const response = await fulfillmentContextService.fetchFulfillmentContexts();
      actions.setFulfillmentContexts(response);
      actions.setFetchFulfillmentContextsLoading(false);
      return response;
    } catch (error) {
      actions.fetchFulfillmentContextsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch asset details',
      });
      actions.setFetchFulfillmentContextsLoading(false);
      return error;
    }
  }),
  bulkLinkOriginToContexts: thunk(async (actions, payload, { injections, getState }) => {
    try {
      const { fulfillmentContextService } = injections;
      const res = await fulfillmentContextService.bulkLinkOriginToContexts(
        payload.originId,
        payload.fulfillmentContextIds,
      );
      actions.setLinkOriginToContextsResults(res);
      return res;
    } catch (error) {
      actions.fetchFulfillmentContextsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to perform linking',
      });
      return error;
    }
  }),
  setLinkOriginToContextsResults: action((state, payload) => {
    state.bulkLinkOriginContextResults = payload;
  }),
  fetchFulfillmentContextsFailure: action((state, error) => {
    console.error(error);
  }),
  archiveFulfillmentContext: thunk(async (_actions, payload, { injections }) => {
    const { fulfillmentContextService } = injections;
    await fulfillmentContextService.deleteFulfillmentContext(payload.id);
  }),
  bulkArchiveFulfillmentContexts: thunk(async (_actions, payload, { injections }) => {
    const { fulfillmentContextService } = injections;
    const succeeded: string[] = [];
    const failed: BulkArchiveFailure[] = [];

    // Sequential by design (see docs/design/2026-04-29-bulk-archive-fc-and-origins.md §4).
    // Abort takes effect at iteration boundaries: the in-flight request completes;
    // remaining ids are recorded as aborted in `failed`.
    for (const id of payload.ids) {
      if (payload.signal?.aborted) {
        failed.push({ id, reason: BULK_ARCHIVE_ABORTED_REASON });
        continue;
      }
      try {
        await fulfillmentContextService.deleteFulfillmentContext(id);
        succeeded.push(id);
      } catch (err) {
        failed.push({ id, reason: extractErrorReason(err) });
      }
    }

    return { succeeded, failed };
  }),
};

export default fulfillmentContextsModel;
